// DS3.5
import React, { useMemo } from 'react';
import c from 'classnames';
import InputErrorMessage from '../../../formControls/lego/InputErrorMessage';
import InputLabelText from '../../../formControls/lego/InputLabelText';
import InputLabelSubText from '../../../formControls/lego/InputLabelSubText';
import FormElement from '../../../formControls/layouts/FormElement';
import noop from 'lodash/noop';
import SelectFieldContent, {
  type SelectFieldContentProps,
} from './SelectFieldContent';
import s from './SelectField.scss';

export interface Props extends Omit<SelectFieldContentProps, 'onSelect'> {
  className?: string;
  label: string;
  subLabel?: React.ReactNode;
  isLabelHidden?: boolean;
  isRequired?: boolean;
  noBottomSpacing?: boolean;
  details?: React.ReactNode;
  error?: string;
  onSelect?: (value: string, event: any) => void;
  showPrefixIcons?: boolean;
}

const SelectField: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  className,
  label,
  subLabel,
  isLabelHidden,
  isRequired,
  details,
  error,
  items,
  value,
  prefix: prefixProp,
  onSelect = noop,
  noBottomSpacing = true,
  showPrefixIcons = false,
  ...rest
}) => {
  const prefix = useMemo(() => {
    if (prefixProp || !showPrefixIcons) {
      return prefixProp;
    }
    const selectedItem = items.find(item => `${item.value}` === `${value}`);
    return selectedItem?.icon;
  }, [prefixProp, showPrefixIcons, items, value]);

  return (
    <div className={c(s['root'], className)}>
      <FormElement
        inputId={id}
        label={
          <InputLabelText isRequired={isRequired} className="label">
            {label}
          </InputLabelText>
        }
        subLabel={
          subLabel && (
            <InputLabelSubText className="sublabel">
              {subLabel}
            </InputLabelSubText>
          )
        }
        isLabelHidden={isLabelHidden}
        noBottomSpacing={noBottomSpacing}
        details={
          details ||
          (error && (
            <InputErrorMessage id={`${id}-error`}>{error}</InputErrorMessage>
          ))
        }
      >
        <SelectFieldContent
          id={id}
          onSelect={onSelect}
          required={isRequired}
          hasError={!!error}
          prefix={prefix}
          items={items}
          value={value}
          {...rest}
        />
      </FormElement>
    </div>
  );
};
SelectField.displayName = 'SelectField';

export default SelectField;
